import { useState, type FormEvent } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormModal } from "./form-modal";
import { changePasswordRequest } from "../../auth/api/authService";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

interface ChangePasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful password change (e.g. to navigate onward). */
  onSuccess?: () => void;
  /**
   * First-login / admin-forced mode: hides Cancel and blocks Escape/outside-click
   * dismissal, so the user cannot get into the app without completing this step.
   */
  forceChange?: boolean;
  /** Overrides the default title/description — useful for the first-login variant. */
  title?: string;
  description?: string;
}

// Mirrors the backend's self-service password policy exactly (see API_REFERENCE.md
// "Password management"): min 8 characters, at least one uppercase, one lowercase,
// one number. No special-character requirement — don't over-validate past what the
// backend actually enforces, or valid passwords get rejected client-side for nothing.
function passwordPolicyError(pw: string): string | null {
  if (pw.length < 8) return i18n.t("components:ChangePasswordModalMsgs.newPasswordMustBeAt");
  if (!/[A-Z]/.test(pw)) return i18n.t("components:ChangePasswordModalMsgs.newPasswordMustIncludeAt");
  if (!/[a-z]/.test(pw)) return i18n.t("components:ChangePasswordModalMsgs.newPasswordMustIncludeAt2");
  if (!/[0-9]/.test(pw)) return i18n.t("components:ChangePasswordModalMsgs.newPasswordMustIncludeAt3");
  return null;
}

function PasswordField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation("components");
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-neutral-700">
        {label}<span className="text-destructive"> *</span>
      </Label>
      <div className="relative">
        <Input id={id} type={visible ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 pr-10" />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
          aria-label={visible ? t("PasswordField.hidePassword") : t("PasswordField.showPassword")}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export function ChangePasswordModal({
  open,
  onOpenChange,
  onSuccess,
  forceChange = false,
  title = "Change password",
  description = "Enter your current password and choose a new one.",
}: ChangePasswordModalProps) {
  const { t } = useTranslation("components");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function resetAndClose() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    onOpenChange(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if ((!forceChange && !currentPassword) || !newPassword || !confirmPassword) {
      setError(t("ChangePasswordModal.allFieldsAreRequired"));
      return;
    }
    const policyError = passwordPolicyError(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("ChangePasswordModal.newPasswordAndConfirmPassword"));
      return;
    }

    setSubmitting(true);
    try {
      await changePasswordRequest(forceChange ? { newPassword } : { currentPassword, newPassword });
      toast.success(t("ChangePasswordModal.passwordUpdatedSuccessfully"));
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setError(message || t("ChangePasswordModal.failedToChangePassword"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <FormModal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          if (forceChange) return; // no dismissal until the password is actually changed
          resetAndClose();
          return;
        }
        onOpenChange(next);
      }}
      preventOutsideClose={forceChange}
      title={title}
      description={description}
      className="sm:max-w-md md:max-w-md lg:max-w-md"
      infoFields={[
        ...(forceChange ? [] : [{ field: "Current password", note: t("ChangePasswordModal.verifiedServerSideBeforeThe") }]),
        { field: "New password", note: t("ChangePasswordModal.replacesYourLoginPasswordImmediately") },
      ]}
      footer={
        <>
          {!forceChange && (
            <Button type="button" variant="outline" onClick={resetAndClose}>
              {t("ChangePasswordModal.cancel")}
            </Button>
          )}
          <Button type="submit" form="change-password-form" disabled={submitting}>
            {submitting ? "Updating..." : t("ChangePasswordModal.updatePassword")}
          </Button>
        </>
      }
    >
      <form id="change-password-form" onSubmit={handleSubmit} className="space-y-5">
        {!forceChange && (
          <PasswordField id="currentPassword" label={t("ChangePasswordModal.currentPassword")} value={currentPassword} onChange={setCurrentPassword} />
        )}
        <PasswordField id="newPassword" label={t("ChangePasswordModal.newPassword")} value={newPassword} onChange={setNewPassword} />
        <PasswordField id="confirmPassword" label={t("ChangePasswordModal.confirmNewPassword")} value={confirmPassword} onChange={setConfirmPassword} />

        <p className="text-sm text-destructive min-h-[1.25rem]">{error || "\u00A0"}</p>
      </form>
    </FormModal>
  );
}